import filter from 'lodash/filter';
import find from 'lodash/find';
import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import AgentAssignedCondition from './conditions/AgentAssignedCondition';
import AgentStatusCondition from './conditions/AgentStatusCondition';
import AnswerCondition from './conditions/AnswerCondition';
import BusinessHoursCondition from './conditions/BusinessHoursCondition';
import EstimatedRemainingWaitTimeCondition from './conditions/EstimatedRemainingWaitTimeCondition';
import Condition, {
  AgentAssignedCondition as AgentAssignedConditionType,
  AgentStatusCondition as AgentStatusConditionType,
  AnswerCondition as AnswerConditionType,
  AppPlatformDataAttributeStringCondition as AppPlatformDataAttributeConditionType,
  AppPlatformDataAttributeCurrencyCondition as AppPlatformDataAttributeCurrencyConditionType,
  AppPlatformDataAttributeNumberCondition as AppPlatformDataAttributeNumberConditionType,
  BusinessHoursCondition as BusinessHoursConditionType,
  ChatCampaignCondition as ChatCampaignConditionType,
  ConditionType,
  CustomAttributeCurrencyCondition as CustomAttributeCurrencyConditionType,
  CustomAttributeNumberCondition as CustomAttributeNumberConditionType,
  CustomAttributeStringCondition as CustomAttributeConditionType,
  CustomerAssigneeCondition as CustomerAssigneeConditionType,
  DueInCondition as DueInConditionType,
  EstimatedRemainingWaitTimeCondition as EstimatedRemainingWaitTimeConditionType,
  invalidConditionsForTrigger,
  MessageChannelCondition as MessageChannelConditionType,
  QueueItemStatusCondition as QueueItemStatusConditionType,
  RoutingChannelCondition as RoutingChannelConditionType,
  RoutingGroupCondition as RoutingGroupConditionType,
  StringMatcherCondition as StringMatcherConditionType,
  SkillCondition as SkillConditionType,
  TopicsCondition as TopicsConditionType,
  WaitTimeCondition as WaitTimeConditionType,
} from 'src/actions/rules/condition';
import AppPlatformDataAttributeCurrencyCondition from './conditions/AppPlatformDataAttributeCurrencyCondition';
import AppPlatformDataAttributeNumberCondition from './conditions/AppPlatformDataAttributeNumberCondition';
import ChatCampaignCondition from './conditions/ChatCampaignCondition';
import CustomerAssigneeCondition from './conditions/CustomerAssigneeCondition';
import CustomAttributeCurrencyCondition from './conditions/CustomAttributeCurrencyCondition';
import CustomAttributeNumberCondition from './conditions/CustomAttributeNumberCondition';
import DueInCondition from './conditions/DueInCondition';
import FirstMessageInConversationCondition from './conditions/FirstMessageInConversationCondition';
import FirstMessageInSessionCondition from './conditions/FirstMessageInSessionCondition';
import { getGroupedMenuOptions } from './shared/getGroupedMenuOptions';
import MessageChannelCondition from './conditions/MessageChannelCondition';
import QueueItemStatusCondition from './conditions/QueueItemStatusCondition';
import RoutingChannelCondition from './conditions/RoutingChannelCondition';
import RoutingGroupCondition from './conditions/RoutingGroupCondition';
import RuleEditorSection, { AddRuleRequirementButton, StyledDropdown, StyledSelectorRow } from './RuleEditorSection';
import RuleSectionErrors from './shared/RuleSectionErrors';
import SkillCondition from './conditions/SkillCondition';
import SpamCondition from './conditions/SpamCondition';
import StringMatcherCondition from './conditions/StringMatcherCondition';
import { StyledRowWrapper } from './shared/StyledComponents';
import TopicsCondition from './conditions/TopicsCondition';
import { TriggerType } from 'src/actions/rules/trigger';
import useAppPlatformDataAttributes from './hooks/useAppPlatformDataAttributes';
import useCustomAttributes from './hooks/useCustomAttributes';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import WaitTimeCondition from './conditions/WaitTimeCondition';

type RuleConditionsType = {
  conditions: Condition[];
  title: string;
  errors: any[];
  isReadOnly?: boolean;
  triggerType: TriggerType;
  onAddCondition: Function;
  onDeleteCondition: Function;
  onUpdateCondition: Function;
};

const RuleConditions: FC<RuleConditionsType> = ({
  conditions,
  title,
  errors,
  isReadOnly,
  triggerType,
  onAddCondition,
  onDeleteCondition,
  onUpdateCondition,
}) => {
  const [hasNewCondition, setHasNewCondition] = useState(false);

  const customAttributes = useCustomAttributes();
  const appPlatformDataAttributes = useAppPlatformDataAttributes({ fetch: false });

  const onSelectConditionType = useCallback(
    conditionType => {
      if (Object.values(ConditionType).includes(conditionType)) {
        onAddCondition(conditionType);
      } else {
        const attr = conditionType;
        const matchingAttribute = find(customAttributes, { attr });
        if (matchingAttribute) {
          onAddCondition(ConditionType.CustomAttribute, attr, matchingAttribute.type);
        } else {
          const attributeType = find(appPlatformDataAttributes, { attrId: attr }).type;
          onAddCondition(ConditionType.AppPlatformDataAttribute, attr, attributeType);
        }
      }
      setHasNewCondition(false);
    },
    [onAddCondition, setHasNewCondition, appPlatformDataAttributes, customAttributes]
  );

  const onInitiateCondition = useCallback(() => {
    setHasNewCondition(true);
  }, [setHasNewCondition]);

  const conditionOptions = useConditionOptions(conditions, triggerType, appPlatformDataAttributes, customAttributes);

  return (
    <RuleEditorSection title={title}>
      <div>
        {conditions.map((c, index) => (
          <StyledRowWrapper key={`ruleCondition-${index}`}>
            <RuleCondition
              condition={c}
              index={index}
              isReadOnly={isReadOnly}
              triggerType={triggerType}
              key={index}
              onDelete={onDeleteCondition}
              onUpdate={onUpdateCondition}
            />
            <RuleSectionErrors errors={errors} attr={`conditions[${index}]`} />
          </StyledRowWrapper>
        ))}
      </div>
      {!isReadOnly ? renderConditionSelector() : null}
      {!isReadOnly ? renderAddButton() : null}
      <RuleSectionErrors errors={filter(errors, { attr: 'conditions' })} attr={'conditions'} />
    </RuleEditorSection>
  );

  function renderConditionSelector() {
    if (!hasNewCondition || conditionOptions.length === 0) return null;

    return (
      <StyledSelectorRow>
        {renderText()}
        <StyledDropdown
          attr="conditions"
          data-aid="ruleConditions-selectButton"
          maxRows={5}
          onSelect={onSelectConditionType}
          options={conditionOptions}
          placeholder="Select condition"
          searchable
          value={null}
        />
      </StyledSelectorRow>
    );
  }

  function renderText() {
    if (conditions.length === 0) {
      return <StyledText>If</StyledText>;
    }
    return 'And';
  }

  function renderAddButton() {
    if (conditionOptions.length === 0) return null;
    return (
      <AddRuleRequirementButton
        data-aid="ruleConditions-addButton"
        text="Add Condition"
        onClick={onInitiateCondition}
      />
    );
  }
};

enum ConditionGroup {
  Agent = 'Agent',
  Answer = 'Answer',
  BusinessHours = 'Business Hours',
  Chat = 'Chat',
  Conversation = 'Conversation',
  Email = 'Email',
  Message = 'Message',
  CustomAttributes = 'Custom Attributes',
}

function useConditionOptions(conditions, triggerType, appPlatformDataAttributes, customAttributes) {
  const isFeatureEnabled = useIsFeatureEnabled();

  const usedConditions = new Set();
  conditions.forEach(c => {
    switch (c.type) {
      case ConditionType.AppPlatformDataAttribute:
        usedConditions.add((c as AppPlatformDataAttributeConditionType).attrId);
        break;
      case ConditionType.CustomAttribute:
        usedConditions.add((c as CustomAttributeConditionType).name);
        break;
      case ConditionType.Channels:
      case ConditionType.MessageChannel:
      case ConditionType.WaitTime:
        usedConditions.add(ConditionType.Channels);
        usedConditions.add(ConditionType.MessageChannel);
        usedConditions.add(ConditionType.WaitTime);
        break;
      default:
        usedConditions.add(c.type);
    }
  });

  if (triggerType in invalidConditionsForTrigger) {
    invalidConditionsForTrigger[triggerType].forEach(type => usedConditions.add(type));
  }

  const options = standardConditionOptions.concat();

  if (isFeatureEnabled('dedicatedHero') && triggerType !== TriggerType.SmartMatch) {
    options.push({
      label: 'Dedicated Hero',
      value: ConditionType.CustomerAssignee,
      group: ConditionGroup.Agent,
    });
  }

  if (triggerType === TriggerType.SmartMatch) {
    options.push({ label: 'Skills', value: ConditionType.Skill, group: ConditionGroup.Agent });
  }

  options.push({
    label: 'Business Hours',
    value: ConditionType.BusinessHours,
    group: ConditionGroup.BusinessHours,
  });

  options.push({
    label: 'URL',
    value: ConditionType.ChatURL,
    group: ConditionGroup.Chat,
  });

  options.push({
    label: 'Proactive Chat',
    value: ConditionType.ChatCampaign,
    group: ConditionGroup.Chat,
  });

  if (isFeatureEnabled('spamRuleCondition')) {
    options.push({
      label: 'Spam',
      value: ConditionType.Spam,
      group: ConditionGroup.Email,
    });
  }

  const customAttributeOptions = customAttributes.map(({ attr, label }) => {
    return { value: attr, label, group: ConditionGroup.CustomAttributes };
  });

  const appPlatformDataAttributeOptions = appPlatformDataAttributes.map(({ attrId, label }) => {
    // App platform data attributes and custom attributes are unified in-product in Rules
    return { value: attrId, label, group: ConditionGroup.CustomAttributes };
  });

  const conditionOptions = options
    .concat(customAttributeOptions)
    .concat(appPlatformDataAttributeOptions)
    .filter(({ value }) => {
      return !usedConditions.has(value);
    });

  return getGroupedMenuOptions(conditionOptions, ConditionGroup);
}

const standardConditionOptions: { label: string; value: string; group: string }[] = [
  { label: 'Agent Status', value: ConditionType.AgentStatus, group: ConditionGroup.Agent },
  { label: 'Answer', value: ConditionType.Answer, group: ConditionGroup.Answer },
  { label: 'Assignment Status', value: ConditionType.AgentAssigned, group: ConditionGroup.Conversation },
  { label: 'Channel', value: ConditionType.MessageChannel, group: ConditionGroup.Message },
  { label: 'Channel Type', value: ConditionType.Channels, group: ConditionGroup.Message },
  { label: 'Due Date', value: ConditionType.DueIn, group: ConditionGroup.Conversation },
  {
    label: 'Estimated Remaining Wait Time',
    value: ConditionType.EstimatedRemainingWaitTime,
    group: ConditionGroup.Conversation,
  },
  { label: 'First Message', value: ConditionType.FirstMessageInConversation, group: ConditionGroup.Message },
  { label: 'Inbox', value: ConditionType.RoutingGroup, group: ConditionGroup.Conversation },
  { label: 'Message Body', value: ConditionType.MessageBody, group: ConditionGroup.Message },
  { label: 'Message To', value: ConditionType.MessageRecipient, group: ConditionGroup.Message },
  { label: 'Message From', value: ConditionType.MessageSender, group: ConditionGroup.Message },
  { label: 'Message Subject', value: ConditionType.MessageSubject, group: ConditionGroup.Message },
  { label: 'New Contact', value: ConditionType.FirstMessageInSession, group: ConditionGroup.Message },
  { label: 'Transfer Status', value: ConditionType.QueueItemStatus, group: ConditionGroup.Conversation },
  { label: 'Topics', value: ConditionType.Topics, group: ConditionGroup.Conversation },
  { label: 'Wait Time', value: ConditionType.WaitTime, group: ConditionGroup.Conversation },
];

type RuleConditionType = {
  condition: Condition;
  index: number;
  isReadOnly?: boolean;
  triggerType: TriggerType;
  onDelete: Function;
  onUpdate: Function;
};

export const RuleCondition: FC<RuleConditionType> = ({
  condition,
  index,
  isReadOnly,
  triggerType,
  onDelete,
  onUpdate,
}) => {
  switch (condition.type) {
    case ConditionType.AgentStatus:
      return (
        <AgentStatusCondition
          condition={condition as AgentStatusConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.AgentAssigned:
      return (
        <AgentAssignedCondition
          condition={condition as AgentAssignedConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.Answer:
      return (
        <AnswerCondition
          condition={condition as AnswerConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.AppPlatformDataAttribute:
      if (condition instanceof AppPlatformDataAttributeCurrencyConditionType) {
        return (
          <AppPlatformDataAttributeCurrencyCondition
            condition={condition as AppPlatformDataAttributeCurrencyConditionType}
            index={index}
            isReadOnly={isReadOnly}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        );
      }
      if (condition instanceof AppPlatformDataAttributeNumberConditionType) {
        return (
          <AppPlatformDataAttributeNumberCondition
            condition={condition as AppPlatformDataAttributeNumberConditionType}
            index={index}
            isReadOnly={isReadOnly}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        );
      }
      return (
        <StringMatcherCondition
          condition={condition as StringMatcherConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.BusinessHours:
      return (
        <BusinessHoursCondition
          condition={condition as BusinessHoursConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.Channels:
      return (
        <RoutingChannelCondition
          condition={condition as RoutingChannelConditionType}
          index={index}
          triggerType={triggerType}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.ChatCampaign:
      return (
        <ChatCampaignCondition
          condition={condition as ChatCampaignConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.CustomerAssignee:
      return (
        <CustomerAssigneeCondition
          condition={condition as CustomerAssigneeConditionType}
          index={index}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.CustomAttribute:
      if (condition instanceof CustomAttributeCurrencyConditionType) {
        return (
          <CustomAttributeCurrencyCondition
            condition={condition as CustomAttributeCurrencyConditionType}
            index={index}
            isReadOnly={isReadOnly}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        );
      }
      if (condition instanceof CustomAttributeNumberConditionType) {
        return (
          <CustomAttributeNumberCondition
            condition={condition as CustomAttributeNumberConditionType}
            index={index}
            isReadOnly={isReadOnly}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        );
      }
      return (
        <StringMatcherCondition
          condition={condition as StringMatcherConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.DueIn:
      return (
        <DueInCondition
          condition={condition as DueInConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.EstimatedRemainingWaitTime:
      return (
        <EstimatedRemainingWaitTimeCondition
          condition={condition as EstimatedRemainingWaitTimeConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.FirstMessageInConversation:
      return <FirstMessageInConversationCondition index={index} onDelete={onDelete} />;
    case ConditionType.FirstMessageInSession:
      return <FirstMessageInSessionCondition index={index} onDelete={onDelete} />;
    case ConditionType.Spam:
      return <SpamCondition index={index} onDelete={onDelete} />;
    case ConditionType.MessageChannel:
      return (
        <MessageChannelCondition
          condition={condition as MessageChannelConditionType}
          triggerType={triggerType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.ChatURL:
    case ConditionType.MessageBody:
    case ConditionType.MessageRecipient:
    case ConditionType.MessageSender:
    case ConditionType.MessageSubject:
      return (
        <StringMatcherCondition
          condition={condition as StringMatcherConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.QueueItemStatus:
      return (
        <QueueItemStatusCondition
          condition={condition as QueueItemStatusConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.RoutingGroup:
      return (
        <RoutingGroupCondition
          condition={condition as RoutingGroupConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.Skill:
      return (
        <SkillCondition
          condition={condition as SkillConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.Topics:
      return (
        <TopicsCondition
          condition={condition as TopicsConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );
    case ConditionType.WaitTime:
      return (
        <WaitTimeCondition
          condition={condition as WaitTimeConditionType}
          index={index}
          isReadOnly={isReadOnly}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      );

    default:
      return null;
  }
};

const StyledText = styled.div`
  margin-right: 8px;
`;

export default RuleConditions;
